import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
    Star, TrendingUp, MessageSquare, ExternalLink, ArrowUpRight,
    Filter, RefreshCw, ThumbsUp, AlertCircle, BarChart3, Eye,
    AlertTriangle, Download, ChevronDown, Shield
} from 'lucide-react';
import { apiFetch } from '../services/cloudflareService';
import InfoTooltip from './InfoTooltip';

interface FeedbackItem {
    id: string;
    hospitalId: string;
    rating: number;
    message: string | null;
    patientCode: string | null;
    createdAt: string;
    hospitalName?: string;
}

interface StatsData {
    totalReviews: number;
    averageRating: number;
    distribution: Record<string, number>;
    fiveStarCount: number;
    googleReviewClicks: number;
    reviewsDismissed: number;
    reviewsShown: number;
    conversionRate: number;
    recentTrend: { week: string; avg: number; count: number }[];
}

interface Props {
    theme: 'dark' | 'light';
    hospitals: any[];
}

const SatisfactionDashboard: React.FC<Props> = ({ theme, hospitals }) => {
    const [selectedHospitalId, setSelectedHospitalId] = useState<string>('all');
    const [selectedRatingFilter, setSelectedRatingFilter] = useState<number | null>(null);
    const [stats, setStats] = useState<StatsData | null>(null);
    const [allStats, setAllStats] = useState<Record<string, StatsData>>({});
    const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [visibleCount, setVisibleCount] = useState(20);

    const t = useMemo(() => ({
        textMain: theme === 'dark' ? 'text-slate-300' : 'text-slate-600',
        textWhite: theme === 'dark' ? 'text-white' : 'text-slate-900',
        textMuted: theme === 'dark' ? 'text-slate-400' : 'text-slate-500',
        cardBg: theme === 'dark' ? 'bg-white/[0.02] backdrop-blur-2xl' : 'bg-white/70 backdrop-blur-2xl',
        cardBorder: theme === 'dark' ? 'border-white/[0.05]' : 'border-slate-200/60',
        cardShadow: theme === 'dark' ? 'shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]' : 'shadow-[inset_0_1px_0_rgba(255,255,255,1),_0_8px_30px_rgba(0,0,0,0.02)]',
        inputBg: theme === 'dark' ? 'bg-black/20' : 'bg-slate-100/50',
    }), [theme]);

    const fetchData = async () => {
        setIsLoading(true);
        setError(null);
        const statsMap: Record<string, StatsData> = {};
        const allFeedback: FeedbackItem[] = [];

        for (const h of hospitals) {
            try {
                const [statsRes, fbRes] = await Promise.all([
                    apiFetch(`/api/feedback/stats?hospitalId=${h.id}`, { credentials: 'include' }),
                    apiFetch(`/api/feedback?hospitalId=${h.id}`, { credentials: 'include' })
                ]);

                if (statsRes.ok) {
                    const data = await statsRes.json();
                    statsMap[h.id] = data;
                }

                if (fbRes.ok) {
                    const items = await fbRes.json();
                    if (Array.isArray(items)) {
                        allFeedback.push(...items.map(i => ({ ...i, hospitalName: h.name })));
                    }
                }
            } catch { }
        }

        setAllStats(statsMap);
        setFeedback(allFeedback.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
        setIsLoading(false);
    };

    useEffect(() => {
        if (hospitals.length > 0) fetchData();
    }, [hospitals]);

    // Compute aggregated or single-hospital stats
    const displayStats = useMemo((): StatsData | null => {
        if (selectedHospitalId !== 'all') {
            return allStats[selectedHospitalId] || null;
        }
        // Aggregate all hospitals
        const entries = Object.values(allStats);
        if (entries.length === 0) return null;

        const merged: StatsData = {
            totalReviews: 0,
            averageRating: 0,
            distribution: { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0 },
            fiveStarCount: 0,
            googleReviewClicks: 0,
            reviewsDismissed: 0,
            reviewsShown: 0,
            conversionRate: 0,
            recentTrend: []
        };

        let ratingSum = 0;
        for (const e of entries as StatsData[]) {
            merged.totalReviews += e.totalReviews;
            ratingSum += e.averageRating * e.totalReviews;
            for (const [k, v] of Object.entries(e.distribution)) {
                merged.distribution[k] = (merged.distribution[k] || 0) + (v as number);
            }
            merged.fiveStarCount += e.fiveStarCount;
            merged.googleReviewClicks += e.googleReviewClicks;
            merged.reviewsDismissed += e.reviewsDismissed;
            merged.reviewsShown += e.reviewsShown;
        }
        merged.averageRating = merged.totalReviews > 0 ? Math.round((ratingSum / merged.totalReviews) * 10) / 10 : 0;
        merged.conversionRate = merged.fiveStarCount > 0 ? Math.round((merged.googleReviewClicks / merged.fiveStarCount) * 100) : 0;

        return merged;
    }, [allStats, selectedHospitalId]);

    // NPS-like Satisfaction Score: (4★ + 5★) / total × 100
    const satisfactionScore = useMemo(() => {
        if (!displayStats || displayStats.totalReviews === 0) return null;
        const positive = (displayStats.distribution['4'] || 0) + (displayStats.distribution['5'] || 0);
        return Math.round((positive / displayStats.totalReviews) * 100);
    }, [displayStats]);

    const displayFeedback = useMemo(() => {
        let filtered = selectedHospitalId === 'all'
            ? feedback
            : feedback.filter(fb => fb.hospitalId === selectedHospitalId);

        if (selectedRatingFilter !== null) {
            filtered = filtered.filter(fb => fb.rating === selectedRatingFilter);
        }
        return filtered;
    }, [feedback, selectedHospitalId, selectedRatingFilter]);

    // Reputation alerts: ≤2★ in last 7 days
    const reputationAlerts = useMemo(() => {
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const cutoff = sevenDaysAgo.toISOString();

        let source = selectedHospitalId === 'all'
            ? feedback
            : feedback.filter(fb => fb.hospitalId === selectedHospitalId);

        return source.filter(fb => fb.rating <= 2 && fb.createdAt >= cutoff);
    }, [feedback, selectedHospitalId]);

    // CSV Export
    const exportCSV = () => {
        const rows = displayFeedback.map(fb => {
            const date = new Date(fb.createdAt);
            const dateStr = date.toLocaleDateString('es-MX', { year: 'numeric', month: '2-digit', day: '2-digit' });
            const timeStr = date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
            const hospital = fb.hospitalName || fb.hospitalId;
            const message = (fb.message || 'Sin comentario').replace(/"/g, '""');
            return `"${dateStr}","${timeStr}","${hospital}","${fb.rating}","${message}","${fb.patientCode || ''}"`;
        });

        const headers = 'Fecha,Hora,Hospital,Estrellas,Comentario,Código Paciente';
        const bom = '\uFEFF';
        const csvContent = bom + headers + '\n' + rows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const filterLabel = selectedRatingFilter ? `_${selectedRatingFilter}estrellas` : '';
        const hospitalLabel = selectedHospitalId !== 'all' ? `_${selectedHospitalId.slice(0, 8)}` : '_todos';
        link.setAttribute('href', url);
        link.setAttribute('download', `satisfaccion${hospitalLabel}${filterLabel}_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    if (isLoading) {
        return (
            <div className="p-8 animate-pulse space-y-6">
                <div className="h-8 w-64 bg-indigo-500/20 rounded-lg" />
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    {[1, 2, 3, 4].map(i => <div key={i} className="h-32 bg-indigo-500/10 rounded-2xl" />)}
                </div>
            </div>
        );
    }

    const maxDistribution = displayStats ? Math.max(...(Object.values(displayStats.distribution) as number[]), 1) : 1;

    const cardClass = `${t.cardBg} rounded-2xl border ${t.cardBorder} ${t.cardShadow} p-6 relative overflow-hidden`;

    // Star filter button helper
    const StarFilterButton: React.FC<{ star: number }> = ({ star }) => {
        const isActive = selectedRatingFilter === star;
        return (
            <button
                onClick={() => setSelectedRatingFilter(isActive ? null : star)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${isActive
                    ? star >= 4 ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' :
                        star === 3 ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
                            'bg-red-500/20 text-red-400 border-red-500/30'
                    : `${theme === 'dark' ? 'bg-white/[0.03] text-slate-400 border-white/[0.05] hover:bg-white/[0.06]' : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'}`
                    }`}
            >
                {star} <Star className={`w-3 h-3 ${isActive ? (star >= 4 ? 'text-emerald-400 fill-emerald-400' : star === 3 ? 'text-amber-400 fill-amber-400' : 'text-red-400 fill-red-400') : 'text-amber-400 fill-amber-400'}`} />
            </button>
        );
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className={`space-y-8 ${t.textMain}`}
        >
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className={`p-2.5 rounded-xl ${theme === 'dark' ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-200'} shadow-[0_0_15px_rgba(245,158,11,0.15)]`}>
                        <Star className="w-6 h-6 text-amber-500" />
                    </div>
                    <div>
                        <h2 className={`text-xl font-black ${t.textWhite} leading-tight`}>Satisfacción & Reputación</h2>
                        <p className={`text-xs ${t.textMuted} font-medium`}>Calificaciones de pacientes y funnel de Google Reviews</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <Filter className={`w-4 h-4 ${t.textMuted} absolute left-3 top-1/2 -translate-y-1/2`} />
                        <select
                            value={selectedHospitalId}
                            onChange={e => setSelectedHospitalId(e.target.value)}
                            className={`pl-9 pr-4 py-2 ${t.inputBg} border ${t.cardBorder} rounded-xl ${t.textWhite} text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 appearance-none cursor-pointer min-w-[180px]`}
                        >
                            <option value="all">Todos los hospitales</option>
                            {hospitals.map(h => (
                                <option key={h.id} value={h.id}>{h.name}</option>
                            ))}
                        </select>
                    </div>
                    <button
                        onClick={exportCSV}
                        disabled={displayFeedback.length === 0}
                        title="Exportar opiniones a CSV"
                        className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold transition-all border ${theme === 'dark' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20 disabled:opacity-30' : 'bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100 disabled:opacity-30'}`}
                    >
                        <Download className="w-4 h-4" /> CSV
                    </button>
                    <button onClick={fetchData} className={`p-2 rounded-lg ${t.cardBg} border ${t.cardBorder} hover:bg-slate-700/50 transition-colors`}>
                        <RefreshCw className={`w-4 h-4 ${t.textMuted}`} />
                    </button>
                </div>
            </div>

            {!displayStats || displayStats.totalReviews === 0 ? (
                <div className={`${cardClass} p-12 text-center flex flex-col items-center justify-center`}>
                    <div className={`w-16 h-16 rounded-full ${theme === 'dark' ? 'bg-amber-500/10' : 'bg-amber-50'} flex items-center justify-center mb-4`}>
                        <Star className="w-8 h-8 text-amber-400" />
                    </div>
                    <h3 className={`text-xl font-bold ${t.textWhite} mb-2`}>Sin calificaciones registradas</h3>
                    <p className={`text-sm ${t.textMuted} max-w-md`}>Las calificaciones aparecerán aquí cuando los pacientes completen su proceso de alta y envíen opiniones.</p>
                </div>
            ) : (
                <>
                    {/* ═══ REPUTATION ALERTS INBOX ═══ */}
                    {reputationAlerts.length > 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className={`rounded-2xl border p-5 relative overflow-hidden ${theme === 'dark' ? 'bg-red-500/[0.03] border-red-500/20' : 'bg-red-50/50 border-red-200/60'}`}
                        >
                            <div className="flex items-center gap-3 mb-4">
                                <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-red-500/10 border border-red-500/20' : 'bg-red-100 border border-red-200'}`}>
                                    <AlertTriangle className="w-5 h-5 text-red-400" />
                                </div>
                                <div>
                                    <h3 className={`text-sm font-bold ${theme === 'dark' ? 'text-red-300' : 'text-red-700'} uppercase tracking-widest flex items-center gap-2`}>
                                        Alertas de Reputación
                                        <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${theme === 'dark' ? 'bg-red-500/20 text-red-300' : 'bg-red-100 text-red-600'}`}>
                                            {reputationAlerts.length}
                                        </span>
                                    </h3>
                                    <p className={`text-[10px] ${t.textMuted}`}>Opiniones de 1-2 estrellas en los últimos 7 días — requieren atención</p>
                                </div>
                            </div>
                            <div className="space-y-2 max-h-[200px] overflow-y-auto scrollbar-hide">
                                {reputationAlerts.slice(0, 10).map((fb, i) => (
                                    <div key={fb.id || i} className={`flex items-start gap-3 p-3 rounded-xl transition-all ${theme === 'dark' ? 'bg-red-500/[0.04] border border-red-500/10' : 'bg-white/80 border border-red-100'}`}>
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${theme === 'dark' ? 'bg-red-500/10' : 'bg-red-50'}`}>
                                            <span className="text-sm font-black text-red-400">{fb.rating}★</span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            {fb.message ? (
                                                <p className={`text-sm font-medium ${t.textWhite}`}>{fb.message}</p>
                                            ) : (
                                                <p className={`text-sm ${t.textMuted} italic`}>Sin comentario</p>
                                            )}
                                            <p className={`text-[10px] ${t.textMuted} mt-1 font-medium`}>
                                                {fb.hospitalName} • {new Date(fb.createdAt).toLocaleDateString('es-MX', { month: 'short', day: 'numeric' })}
                                                {' '}{new Date(fb.createdAt).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                                                {fb.patientCode && <span className={`ml-2 px-1.5 py-0.5 rounded ${theme === 'dark' ? 'bg-white/5' : 'bg-slate-200'} font-mono`}>{fb.patientCode}</span>}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    )}

                    {/* ═══ KPI ROW ═══ */}
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                        {/* Average Rating */}
                        <div className={`${cardClass} group`}>
                            <div className="flex items-center gap-1 mb-4">
                                {[1, 2, 3, 4, 5].map(s => (
                                    <Star key={s} className={`w-4 h-4 transition-all duration-300 ${s <= Math.round(displayStats.averageRating) ? 'text-amber-400 fill-amber-400 group-hover:scale-110' : `${t.textMuted} opacity-20`}`} />
                                ))}
                            </div>
                            <p className={`text-4xl font-black ${t.textWhite} tracking-tight`}>{displayStats.averageRating}</p>
                            <p className={`text-[10px] font-medium ${t.textMuted} tracking-wide mt-1 flex items-center gap-1`}>
                                Rating Promedio
                                <InfoTooltip theme={theme} text="Promedio de estrellas (1-5) de todas las opiniones enviadas por pacientes al finalizar su proceso quirúrgico." />
                            </p>
                        </div>

                        {/* Satisfaction Score (NPS-like) */}
                        <div className={`${cardClass} group`}>
                            <div className={`p-2 rounded-lg ${satisfactionScore !== null && satisfactionScore >= 80
                                    ? (theme === 'dark' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-emerald-50 text-emerald-600 border border-emerald-200')
                                    : satisfactionScore !== null && satisfactionScore >= 60
                                        ? (theme === 'dark' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : 'bg-amber-50 text-amber-600 border border-amber-200')
                                        : (theme === 'dark' ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-red-50 text-red-600 border border-red-200')
                                } w-fit mb-3 group-hover:scale-110 transition-transform`}>
                                <Shield className="w-5 h-5" />
                            </div>
                            <p className={`text-4xl font-black ${t.textWhite} tracking-tight`}>{satisfactionScore ?? '—'}%</p>
                            <p className={`text-[10px] font-medium ${t.textMuted} tracking-wide mt-1 flex items-center gap-1`}>
                                Satisfaction Score
                                <InfoTooltip theme={theme} text="Porcentaje de pacientes que dieron 4 o 5 estrellas. Un score ≥80% es excelente, ≥60% aceptable, <60% requiere acción. Fórmula: (4★ + 5★) ÷ Total × 100." />
                            </p>
                        </div>

                        {/* Total Reviews */}
                        <div className={`${cardClass}`}>
                            <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20' : 'bg-indigo-50 text-indigo-600 border border-indigo-200'} w-fit mb-3`}>
                                <MessageSquare className="w-5 h-5" />
                            </div>
                            <p className={`text-4xl font-black ${t.textWhite} tracking-tight`}>{displayStats.totalReviews}</p>
                            <p className={`text-[10px] font-medium ${t.textMuted} tracking-wide mt-1 flex items-center gap-1`}>
                                Opiniones Totales
                                <InfoTooltip theme={theme} text="Total de evaluaciones enviadas por familiares de pacientes (1 a 5 estrellas) en el periodo seleccionado." />
                            </p>
                        </div>

                        {/* Google Review Clicks */}
                        <div className={`${cardClass}`}>
                            <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-emerald-50 text-emerald-600 border border-emerald-200'} w-fit mb-3`}>
                                <ExternalLink className="w-5 h-5" />
                            </div>
                            <p className={`text-4xl font-black ${t.textWhite} tracking-tight`}>{displayStats.googleReviewClicks}</p>
                            <p className={`text-[10px] font-medium ${t.textMuted} tracking-wide mt-1 flex items-center gap-1`}>
                                Clicks a Google
                                <InfoTooltip theme={theme} text="Veces que un familiar de 5 estrellas presionó el botón para dejar su reseña en Google Reviews. Es un indicador de intención de reputación." />
                            </p>
                        </div>

                        {/* Conversion */}
                        <div className={`${cardClass}`}>
                            <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20' : 'bg-purple-50 text-purple-600 border border-purple-200'} w-fit mb-3`}>
                                <ArrowUpRight className="w-5 h-5" />
                            </div>
                            <p className={`text-4xl font-black ${t.textWhite} tracking-tight`}>{displayStats.conversionRate}%</p>
                            <p className={`text-[10px] font-medium ${t.textMuted} tracking-wide mt-1 flex items-center gap-1`}>
                                Conversión Google
                                <InfoTooltip theme={theme} text="Porcentaje de opiniones de 5 estrellas que derivaron en un click a Google Reviews. Fórmula: Clicks a Google ÷ Opiniones de 5★ × 100." />
                            </p>
                        </div>
                    </div>

                    {/* ═══ Distribution + Funnel ═══ */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Star Distribution */}
                        <div className={`${cardClass}`}>
                            <h3 className={`text-sm font-bold uppercase tracking-widest ${t.textWhite} mb-6 flex items-center gap-2`}>
                                <BarChart3 className="w-4 h-4 text-amber-400" /> Distribución de Estrellas
                            </h3>
                            <div className="space-y-3">
                                {[5, 4, 3, 2, 1].map(star => {
                                    const count = displayStats.distribution[String(star)] || 0;
                                    const pct = displayStats.totalReviews > 0 ? Math.round((count / displayStats.totalReviews) * 100) : 0;
                                    const isSelected = selectedRatingFilter === star;
                                    return (
                                        <button
                                            key={star}
                                            onClick={() => setSelectedRatingFilter(isSelected ? null : star)}
                                            className={`w-full flex items-center gap-3 px-2 py-1.5 -mx-2 rounded-lg transition-all ${isSelected ? (theme === 'dark' ? 'bg-white/10' : 'bg-slate-100') : 'hover:bg-slate-500/10'}`}
                                        >
                                            <div className="flex items-center gap-1 w-16 flex-shrink-0 text-left">
                                                <span className={`text-sm font-bold ${t.textWhite}`}>{star}</span>
                                                <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                                            </div>
                                            <div className={`flex-1 h-5 rounded-full overflow-hidden ${theme === 'dark' ? 'bg-white/[0.05]' : 'bg-slate-100'}`}>
                                                <motion.div
                                                    initial={{ width: 0 }}
                                                    animate={{ width: `${pct}%` }}
                                                    transition={{ duration: 0.8, delay: (5 - star) * 0.1, ease: 'easeOut' }}
                                                    className={`h-full rounded-full ${star >= 4 ? 'bg-gradient-to-r from-emerald-500 to-emerald-400' :
                                                            star === 3 ? 'bg-gradient-to-r from-amber-500 to-amber-400' :
                                                                'bg-gradient-to-r from-red-500 to-red-400'
                                                        }`}
                                                />
                                            </div>
                                            <span className={`text-xs font-bold ${t.textMuted} w-16 text-right`}>{count} ({pct}%)</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Review Funnel */}
                        <div className={`${cardClass}`}>
                            <h3 className={`text-sm font-bold uppercase tracking-widest ${t.textWhite} mb-6 flex items-center gap-2`}>
                                <TrendingUp className="w-4 h-4 text-emerald-400" /> Funnel de Google Reviews
                            </h3>
                            <div className="space-y-4">
                                {[
                                    { label: 'Encuestas Mostradas', value: displayStats.reviewsShown, color: 'indigo', icon: Eye },
                                    { label: 'Opiniones Enviadas', value: displayStats.totalReviews, color: 'blue', icon: MessageSquare },
                                    { label: '5 Estrellas (Redirigibles)', value: displayStats.fiveStarCount, color: 'amber', icon: Star },
                                    { label: 'Clicks a Google Reviews', value: displayStats.googleReviewClicks, color: 'emerald', icon: ExternalLink },
                                ].map((item, i) => {
                                    const pct = item.value > 0 && displayStats.reviewsShown > 0 ? Math.round((item.value / displayStats.reviewsShown) * 100) : (i === 0 ? 100 : 0);
                                    return (
                                        <div key={item.label}>
                                            <div className="flex items-center justify-between mb-1.5">
                                                <div className="flex items-center gap-2">
                                                    <item.icon className={`w-3.5 h-3.5 text-${item.color}-400`} />
                                                    <span className={`text-xs font-bold ${t.textWhite}`}>{item.label}</span>
                                                </div>
                                                <span className={`text-sm font-black ${t.textWhite}`}>{item.value}</span>
                                            </div>
                                            <div className={`w-full h-2 rounded-full overflow-hidden ${theme === 'dark' ? 'bg-white/[0.05]' : 'bg-slate-100'}`}>
                                                <motion.div
                                                    initial={{ width: 0 }}
                                                    animate={{ width: `${pct}%` }}
                                                    transition={{ duration: 0.8, delay: i * 0.15, ease: 'easeOut' }}
                                                    className={`h-full rounded-full bg-${item.color}-500`}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className={`mt-6 p-3 rounded-xl ${theme === 'dark' ? 'bg-emerald-500/5 border border-emerald-500/20' : 'bg-emerald-50 border border-emerald-200'} text-center`}>
                                <p className={`text-xs font-bold text-emerald-500`}>
                                    <ThumbsUp className="w-3.5 h-3.5 inline mr-1" />
                                    {displayStats.conversionRate}% de tus pacientes 5⭐ abren Google Reviews
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* ═══ FEEDBACK LIST WITH FILTERS ═══ */}
                    <div className={`${cardClass}`}>
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                            <h3 className={`text-sm font-bold uppercase tracking-widest ${t.textWhite} flex items-center gap-2`}>
                                <MessageSquare className="w-4 h-4 text-indigo-400" /> Todas las Opiniones
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${theme === 'dark' ? 'bg-white/10 text-slate-300' : 'bg-indigo-100 text-indigo-600'}`}>
                                    {displayFeedback.length}
                                </span>
                            </h3>
                            {/* Explicit Star Filter Buttons */}
                            <div className="flex items-center gap-2">
                                <span className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-wider mr-1`}>Filtrar:</span>
                                {[1, 2, 3, 4, 5].map(s => <StarFilterButton key={s} star={s} />)}
                                {selectedRatingFilter !== null && (
                                    <button
                                        onClick={() => setSelectedRatingFilter(null)}
                                        className={`text-[10px] font-bold px-2 py-1.5 rounded-lg ${theme === 'dark' ? 'text-slate-400 hover:text-white hover:bg-white/5' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'} transition-all`}
                                    >
                                        Limpiar
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="space-y-2 max-h-[500px] overflow-y-auto scrollbar-hide">
                            {displayFeedback.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-10 text-center">
                                    <MessageSquare className={`w-8 h-8 ${t.textMuted} mb-3 opacity-30`} />
                                    <p className={`text-sm font-bold ${t.textWhite}`}>Sin opiniones {selectedRatingFilter ? `de ${selectedRatingFilter} estrellas` : ''}</p>
                                    <p className={`text-xs ${t.textMuted} mt-1`}>Ajusta los filtros para ver resultados</p>
                                </div>
                            ) : (
                                displayFeedback.slice(0, visibleCount).map((fb, i) => (
                                    <div key={fb.id || i} className={`flex items-start gap-4 p-4 rounded-xl transition-all ${theme === 'dark' ? 'bg-white/[0.02] border border-white/[0.05] hover:bg-white/[0.04]' : 'bg-slate-50 border border-slate-100 hover:bg-slate-100/80'}`}>
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${fb.rating >= 4 ? (theme === 'dark' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600') :
                                                fb.rating === 3 ? (theme === 'dark' ? 'bg-amber-500/10 text-amber-400' : 'bg-amber-50 text-amber-600') :
                                                    (theme === 'dark' ? 'bg-red-500/10 text-red-400' : 'bg-red-50 text-red-600')
                                            }`}>
                                            <Star className="w-5 h-5" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <div className="flex gap-0.5">
                                                    {[1, 2, 3, 4, 5].map(s => (
                                                        <Star key={s} className={`w-3 h-3 ${s <= fb.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-600'}`} />
                                                    ))}
                                                </div>
                                                {fb.patientCode && (
                                                    <span className={`text-[10px] font-mono font-bold ${t.textMuted} px-1.5 py-0.5 rounded ${theme === 'dark' ? 'bg-white/5' : 'bg-slate-200'}`}>
                                                        {fb.patientCode}
                                                    </span>
                                                )}
                                            </div>
                                            {fb.message ? (
                                                <p className={`text-sm ${t.textMain}`}>{fb.message}</p>
                                            ) : (
                                                <p className={`text-sm ${t.textMuted} italic`}>Sin comentario</p>
                                            )}
                                            <p className={`text-[10px] ${t.textMuted} mt-1.5 font-medium`}>
                                                {fb.hospitalName} • {new Date(fb.createdAt).toLocaleDateString('es-MX', { month: 'long', day: 'numeric', year: 'numeric' })}
                                                {' '}{new Date(fb.createdAt).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                                            </p>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                        {/* Load More */}
                        {displayFeedback.length > visibleCount && (
                            <button
                                onClick={() => setVisibleCount(prev => prev + 30)}
                                className={`mt-4 w-full py-3 text-center text-xs font-bold uppercase tracking-widest rounded-xl transition-all ${theme === 'dark' ? 'text-indigo-400 hover:bg-indigo-500/10 border border-white/[0.05]' : 'text-indigo-600 hover:bg-indigo-50 border border-slate-200'}`}
                            >
                                Cargar más opiniones ({displayFeedback.length - visibleCount} restantes)
                            </button>
                        )}
                    </div>
                </>
            )}
        </motion.div>
    );
};

export default SatisfactionDashboard;
